import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiMail, FiTrash2, FiPlus } from 'react-icons/fi';
import { useTenant } from '../../context/TenantContext';
import { tenantDataService } from '../../services/tenantDataService';
import { invoiceService } from '../../services/invoiceService';
import toast from 'react-hot-toast';

export default function InvoiceDetail() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    loadInvoiceData();
    loadClients();
  }, [invoiceId, currentTenant]);

  const loadInvoiceData = async () => {
    if (!currentTenant?.id || !invoiceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      tenantDataService.setTenantContext(currentTenant.id);
      const invoiceData = await tenantDataService.getById('invoices', invoiceId);
      
      if (invoiceData) {
        // Convert Firestore timestamps to readable dates
        if (invoiceData.date?.seconds) {
          invoiceData.date = new Date(invoiceData.date.seconds * 1000).toISOString().split('T')[0];
        }
        if (invoiceData.dueDate?.seconds) {
          invoiceData.dueDate = new Date(invoiceData.dueDate.seconds * 1000).toISOString().split('T')[0];
        }
        setInvoice(invoiceData);
      } else {
        toast.error('Invoice not found');
        navigate('/admin?tab=financial');
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
      toast.error('Failed to load invoice');
      navigate('/admin?tab=financial');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    if (!currentTenant?.id) return;

    try {
      tenantDataService.setTenantContext(currentTenant.id);
      const clientsData = await tenantDataService.getAll('clients');
      setClients(clientsData || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setInvoice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addItem = () => {
    setInvoice(prev => ({
      ...prev,
      items: [...(prev.items || []), {
        description: '',
        quantity: 1,
        rate: 0,
        amount: 0
      }]
    }));
  };

  const removeItem = (index) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    if (!invoice?.items) return 0;
    const total = invoice.items.reduce((sum, item) => {
      const amount = parseFloat(item.quantity || 0) * parseFloat(item.rate || 0);
      return sum + amount;
    }, 0);
    return parseFloat(total.toFixed(2));
  };

  const handleSave = async () => {
    if (!invoice || !currentTenant?.id) return;

    try {
      setSaving(true);
      
      // Calculate total
      const total = calculateTotal();
      
      // Prepare invoice data
      const invoiceData = {
        ...invoice,
        total: total,
        updatedAt: new Date()
      };

      // Convert date strings back to Date objects for Firestore
      if (invoiceData.date) {
        invoiceData.date = new Date(invoiceData.date);
      }
      if (invoiceData.dueDate) {
        invoiceData.dueDate = new Date(invoiceData.dueDate);
      }

      tenantDataService.setTenantContext(currentTenant.id);
      await tenantDataService.update('invoices', invoiceId, invoiceData);
      
      toast.success('Invoice updated successfully');
      navigate('/admin?tab=financial');
    } catch (error) {
      console.error('Error saving invoice:', error);
      toast.error('Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async () => {
    if (!invoice || !currentTenant?.id) return;

    try {
      await invoiceService.sendInvoiceEmail(invoiceId);
      toast.success('Invoice email sent successfully');
    } catch (error) {
      console.error('Error sending invoice email:', error);
      toast.error('Failed to send invoice email');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-600">Loading invoice...</div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-600">Invoice not found</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin?tab=financial')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
          >
            <FiArrowLeft size={20} />
            <span>Back to Financial Dashboard</span>
          </button>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleSendEmail}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FiMail size={16} />
            <span>Send Email</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <FiSave size={16} />
            <span>{saving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>

      {/* Invoice Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Edit Invoice</h2>
        
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Number</label>
            <input
              type="text"
              value={invoice.invoiceNumber || ''}
              onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
            <select
              value={invoice.clientId || ''}
              onChange={(e) => {
                const selectedClient = clients.find(c => c.id === e.target.value);
                handleInputChange('clientId', e.target.value);
                handleInputChange('clientName', selectedClient?.name || '');
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Select Client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
            <input
              type="date"
              value={invoice.date || ''}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
            <input
              type="date"
              value={invoice.dueDate || ''}
              onChange={(e) => handleInputChange('dueDate', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={invoice.status || 'unpaid'}
              onChange={(e) => handleInputChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
        </div>

        {/* Items */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Items</h3>
            <button
              onClick={addItem}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <FiPlus size={16} />
              <span>Add Item</span>
            </button>
          </div>
          
          <div className="space-y-3">
            {(invoice.items || []).map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-5">
                  <input
                    type="text"
                    placeholder="Description"
                    value={item.description || ''}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity || ''}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Rate"
                    value={item.rate || ''}
                    onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={`£${((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)).toFixed(2)}`}
                    readOnly
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                  />
                </div>
                <div className="col-span-1">
                  <button
                    onClick={() => removeItem(index)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Total */}
        <div className="border-t pt-4">
          <div className="flex justify-end">
            <div className="text-right">
              <div className="text-lg font-semibold text-gray-900">
                Total: £{calculateTotal().toFixed(2)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
